import type { StyleSpecification } from 'maplibre-gl';
import type { MapSettings, TileSourceKind } from '@/store/settings';

/** A minimal dark vector style that uses Protomaps' demo basemaps via pmtiles. */
const PMTILES_DARK_STYLE: StyleSpecification = {
  version: 8,
  glyphs: 'https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf',
  sprite: 'https://protomaps.github.io/basemaps-assets/sprites/v3/dark',
  sources: {},
  layers: [
    {
      id: 'background',
      type: 'background',
      paint: { 'background-color': '#0B0F1A' },
    },
  ],
};

/** Returns the MapLibre style object/URL given current settings. */
export function buildStyle(s: MapSettings): string | StyleSpecification {
  switch (s.source) {
    case 'openfreemap':
      return s.styleUrl || 'https://tiles.openfreemap.org/styles/liberty';

    case 'maptiler': {
      const base = s.styleUrl || 'streets-v2';
      const styleId = base.includes('http')
        ? base
        : `https://api.maptiler.com/maps/${base}/style.json?key=${s.apiKey}`;
      return styleId;
    }

    case 'mapbox':
      return s.styleUrl || 'mapbox://styles/mapbox/dark-v11';

    case 'pmtiles': {
      // Reference a local PMTiles file via the pmtiles:// protocol — registered
      // with maplibre on app boot in map/protocol.ts.
      const url = s.pmtilesUrl || '/tiles/region.pmtiles';
      return {
        ...PMTILES_DARK_STYLE,
        sources: {
          protomaps: {
            type: 'vector',
            url: `pmtiles://${absolutize(url)}`,
            attribution:
              '<a href="https://protomaps.com">Protomaps</a> | <a href="https://openstreetmap.org">OSM</a>',
          },
        },
        // Sprint 2 fallback: black background only. Sprint 3+ will add
        // pre-baked Protomaps Basemaps layers for full streets/labels.
        layers: PMTILES_DARK_STYLE.layers,
      };
    }

    case 'custom':
      return s.styleUrl;

    default:
      return 'https://tiles.openfreemap.org/styles/liberty';
  }
}

/** Returns true if the source needs terrain DEM URL. */
export function supportsTerrain(_kind: TileSourceKind): boolean {
  return true;
}

/** Convert a relative `/tiles/...` URL into an absolute one so MapLibre's
 *  PMTiles protocol resolver fetches the correct origin. */
function absolutize(url: string): string {
  if (url.startsWith('http') || url.startsWith('pmtiles')) return url;
  if (typeof window === 'undefined') return url;
  return `${window.location.origin}${url}`;
}
